import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PlayerMediaManager from "@/components/PlayerMediaManager";
import ImageUploadField from "@/components/ImageUploadField";
import TeamSelector from "@/components/TeamSelector";
import { toast } from "sonner";
import {
  User, Trophy, TrendingUp, Award, Clock, CalendarDays, ExternalLink, Copy, Share2,
  CheckCircle2, Circle, ShieldCheck, Star, Link as LinkIcon, FileText,
} from "lucide-react";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "edit", label: "Edit Profile" },
  { key: "media", label: "Media" },
  { key: "recruiting", label: "Recruiting & Social" },
  { key: "privacy", label: "Privacy" },
];

const EDITABLE = [
  "first_name", "last_name", "graduation_year", "school", "province", "city",
  "height_cm", "weight_kg", "gpa", "bats", "throws", "primary_position", "secondary_position",
  "instagram_handle", "x_handle", "tiktok_handle", "youtube_handle",
  "fieldlevel_url", "ncsa_url", "img_url", "perfect_game_url", "prep_baseball_url",
  "personal_website_url", "hudl_url", "highlight_video_url",
  "profile_photo_url", "public_visible", "media_release_consent", "social_tagging_enabled",
  "date_of_birth", "current_team_id", "show_age_publicly",
];

export default function PlayerDashboard() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("overview");

  const load = useCallback(() => {
    api.get("/players/me/dashboard")
      .then(({ data }) => { setDash(data); setErr(""); })
      .catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Could not load your dashboard."));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="label-overline text-[#E63946]">Player Dashboard</div>
          <h1 className="font-display text-3xl uppercase tracking-tighter mt-2">No Linked Profile</h1>
          <p data-testid="dashboard-error" className="text-sm text-slate-600 mt-3">{err}</p>
          <Link to="/dashboard" className="inline-block mt-6 text-xs font-semibold uppercase tracking-wider text-[#0A192F]">Go to Account Dashboard →</Link>
        </div>
      </PublicLayout>
    );
  }
  if (!dash) return <PublicLayout><div className="p-16 text-center label-overline">Loading your dashboard…</div></PublicLayout>;

  const p = dash.player;
  const c = dash.completion;
  const w = dash.widgets;
  const publicUrl = `${window.location.origin}/prospects/${p.slug}`;

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-[#0A192F] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center gap-6">
          <div className="w-20 h-20 rounded-sm overflow-hidden bg-white/10 shrink-0">
            {p.profile_photo_url ? <img src={p.profile_photo_url} alt={p.first_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-8 h-8 text-white/40" /></div>}
          </div>
          <div className="min-w-0">
            <div className="label-overline text-white/60">My Baseball Home</div>
            <h1 data-testid="player-dashboard-title" className="font-display text-4xl lg:text-5xl uppercase tracking-tighter mt-1">{p.first_name} {p.last_name}</h1>
            <p className="text-sm text-white/70 mt-1">{[p.primary_position, p.graduation_year && `Class of ${p.graduation_year}`, p.province].filter(Boolean).join(" · ")}</p>
          </div>
          {p.complete_recruiting_profile && (
            <span data-testid="dashboard-complete-badge" className="ml-auto inline-flex items-center gap-1.5 bg-emerald-500 text-white text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-sm">
              <Award className="w-4 h-4" /> Complete Recruiting Profile
            </span>
          )}
        </div>
      </section>

      {/* Quick nav */}
      <section className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-wrap items-center gap-2 py-3">
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="nav-view-public" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider border border-slate-300 px-3 py-2 rounded-sm hover:bg-slate-50"><ExternalLink className="w-3.5 h-3.5" /> Public Profile</a>
          <button data-testid="nav-copy-link" onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success("Profile link copied"); }} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider border border-slate-300 px-3 py-2 rounded-sm hover:bg-slate-50"><Copy className="w-3.5 h-3.5" /> Copy Link</button>
          <button data-testid="nav-share" onClick={() => { if (navigator.share) navigator.share({ title: `${p.first_name} ${p.last_name} · CBR Prospects`, url: publicUrl }).catch(() => {}); else { navigator.clipboard?.writeText(publicUrl); toast.success("Profile link copied"); } }} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider border border-slate-300 px-3 py-2 rounded-sm hover:bg-slate-50"><Share2 className="w-3.5 h-3.5" /> Share</button>
          <a href={`${publicUrl}#timeline`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider border border-slate-300 px-3 py-2 rounded-sm hover:bg-slate-50"><Clock className="w-3.5 h-3.5" /> Timeline</a>
          <a href={`${window.location.origin}/portfolio/${p.slug}`} target="_blank" rel="noreferrer" data-testid="nav-portfolio" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-[#E63946] text-white px-3 py-2 rounded-sm hover:bg-[#c62f3b]"><FileText className="w-3.5 h-3.5" /> Recruiting Portfolio</a>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-[#F8F9FA] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`dashboard-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`shrink-0 px-4 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 ${tab === t.key ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-8 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {tab === "overview" && <Overview p={p} c={c} w={w} onGoEdit={() => setTab("edit")} />}
          {tab === "edit" && <EditProfile p={p} onSaved={load} />}
          {tab === "media" && (
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <p className="text-xs text-slate-500 mb-3">Upload videos and photos. New uploads are <span className="font-semibold">Pending</span> until CBR staff approve them. You can set your Featured Video from approved media.</p>
              <PlayerMediaManager playerId={p.id} mode="player" />
            </div>
          )}
          {tab === "recruiting" && <RecruitingLinks p={p} onSaved={load} />}
          {tab === "privacy" && <Privacy p={p} onSaved={load} />}
        </div>
      </section>
    </PublicLayout>
  );
}

/* ---------------- Overview ---------------- */
function Overview({ p, c, w, onGoEdit }) {
  const m = w.metrics || {};
  const r = w.rankings || {};
  const rankRow = (label, rank, total) => rank ? (
    <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-data text-[#0A192F]">#{rank}<span className="text-slate-400 text-xs"> / {total}</span></span>
    </div>
  ) : null;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Left: completion + widgets */}
      <div className="lg:col-span-2 space-y-6">
        <RecruitingActivity playerId={p.id} />
        <ProfileStrength c={c} onGoEdit={onGoEdit} />

        <Card title="Verified Metrics" icon={TrendingUp} note="Managed by CBR staff">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Exit Velo" data={m.exit_velocity} />
            <Metric label="Arm Velo" data={m.arm_velocity} />
            <Metric label="60 Time" data={m.sixty_yard_sprint} />
            <Metric label="FB Velo" data={m.fastball_velocity} />
          </div>
        </Card>

        <div className="grid sm:grid-cols-2 gap-6">
          <Card title="Rankings" icon={Trophy} note="Managed by CBR staff">
            {r && r.rating != null ? (
              <div data-testid="widget-rankings">
                {rankRow("National", r.national, r.national_total)}
                {rankRow(`Provincial (${r.province || ""})`, r.provincial, r.provincial_total)}
                {rankRow(`Position (${r.primary_position || ""})`, r.position, r.position_total)}
                {rankRow(`Class of ${r.graduation_year || ""}`, r.graduation_year_rank, r.graduation_year_total)}
              </div>
            ) : <Empty text="No ranking yet — CBR staff assign ratings after evaluations." />}
          </Card>

          <Card title="Awards" icon={Award} note="Managed by CBR staff">
            {w.awards?.total > 0 ? (
              <ul className="space-y-1.5" data-testid="widget-awards">
                {w.awards.items.slice(0, 6).map((a) => (
                  <li key={a.id} className="text-sm flex items-center gap-2"><Award className="w-3.5 h-3.5 text-[#E63946] shrink-0" /> <span className="truncate">{a.award_label}</span></li>
                ))}
              </ul>
            ) : <Empty text="No awards yet." />}
          </Card>
        </div>

        <Card title="Career Timeline" icon={Clock} note="Managed by CBR staff">
          {w.timeline?.length > 0 ? (
            <>
              <ul className="space-y-2" data-testid="widget-timeline">
                {w.timeline.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#E63946] shrink-0" />
                    <div><div className="font-semibold">{t.title}</div><div className="text-xs text-slate-400">{t.date ? new Date(t.date).toLocaleDateString("en-CA", { dateStyle: "medium" }) : ""}</div></div>
                  </li>
                ))}
              </ul>
              <Link to={`/prospects/${p.slug}#timeline`} className="inline-block mt-3 text-xs font-semibold uppercase tracking-wider text-[#E63946]">View Full Timeline →</Link>
            </>
          ) : <Empty text="Your timeline builds automatically as you attend camps and earn recognition." />}
        </Card>
      </div>

      {/* Right: upcoming events */}
      <div className="space-y-6">
        <Card title="Upcoming Events" icon={CalendarDays}>
          <Section label="Registered Camps">
            {w.upcoming?.camps?.length ? w.upcoming.camps.map((c2) => (
              <Row key={c2.camp_id} title={c2.title} sub={c2.start_date ? new Date(c2.start_date).toLocaleDateString("en-CA") : ""} tag={c2.status} to={`/camps/${c2.slug}`} />
            )) : <Empty text="No camp registrations." />}
          </Section>
          <Section label="CBR Nation Invitations">
            {w.upcoming?.nation?.length ? w.upcoming.nation.map((n) => (
              <Row key={n.team_id} title={n.team_name || "Team"} sub={[n.season, n.division].filter(Boolean).join(" · ")} tag={n.status} />
            )) : <Empty text="No team invitations." />}
          </Section>
          <Section label="Upcoming Tournaments">
            {w.upcoming?.tournaments?.length ? w.upcoming.tournaments.map((t) => (
              <Row key={t.id} title={t.name} sub={[t.location, t.start_date && new Date(t.start_date).toLocaleDateString("en-CA")].filter(Boolean).join(" · ")} />
            )) : <Empty text="No tournaments scheduled." />}
          </Section>
        </Card>
      </div>
    </div>
  );
}

/* ---------------- Recruiting Activity (analytics widget) ---------------- */
function RecruitingActivity({ playerId }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/players/me/analytics").then(({ data }) => setA(data.widget)).catch(() => setA(null));
  }, [playerId]);
  if (!a) return null;
  const stats = [
    { icon: "👀", label: "Profile Views", value: a.profile_views, testid: "ra-profile-views" },
    { icon: "📄", label: "Portfolio Views", value: a.portfolio_views, testid: "ra-portfolio-views" },
    { icon: "⭐", label: "Recruiters Following", value: a.recruiters_following, testid: "ra-recruiters-following" },
    { icon: "📋", label: "Watch Lists", value: a.watchlists, testid: "ra-watchlists" },
    { icon: "🔗", label: "Portfolio Shares", value: a.portfolio_shares, testid: "ra-portfolio-shares" },
    { icon: "🖱️", label: "Link Clicks", value: a.link_clicks, testid: "ra-link-clicks" },
    { icon: "▶️", label: "Video Views", value: a.video_views, testid: "ra-video-views" },
  ];
  return (
    <div className="bg-gradient-to-br from-[#0A192F] to-[#12233f] text-white rounded-sm p-5" data-testid="recruiting-activity">
      <div className="flex items-center gap-2 label-overline text-white/60"><TrendingUp className="w-4 h-4 text-[#E63946]" /> Recruiting Activity</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-4">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testid} className="bg-white/5 rounded-sm p-3 text-center">
            <div className="text-xl">{s.icon}</div>
            <div className="font-data text-3xl mt-1">{s.value ?? 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5 leading-tight">{s.label}</div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-white/30 mt-3">Your recruiting profile is being discovered. Aggregate engagement only — recruiter identities are never shown.</p>
    </div>
  );
}

function ProfileStrength({ c, onGoEdit }) {
  const pct = c.percent;
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-[#E63946]";
  const dot = pct >= 80 ? "🟢" : pct >= 50 ? "🟡" : "🔴";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="profile-strength">
      <div className="flex items-center justify-between">
        <div className="label-overline flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E63946]" /> Profile Strength</div>
        <div data-testid="profile-strength-percent" className="font-data text-2xl text-[#0A192F]">{dot} {pct}% Complete</div>
      </div>
      <div className="mt-3 h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {c.complete ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700"><Award className="w-4 h-4" /> You've earned the <span className="font-semibold">Complete Recruiting Profile</span> badge!</div>
      ) : (
        <>
          <div className="label-overline text-[10px] mt-4 mb-2">Complete these to boost your profile</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="profile-missing-list">
            {c.items.map((it) => (
              <div key={it.key} className={`flex items-center gap-2 text-sm ${it.done ? "text-slate-400" : "text-slate-700"}`}>
                {it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                <span className={it.done ? "line-through" : ""}>{it.label.replace(/^Add |^Upload /, "")}</span>
              </div>
            ))}
          </div>
          <button data-testid="profile-strength-edit" onClick={onGoEdit} className="mt-4 bg-[#0A192F] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm">Improve My Profile</button>
        </>
      )}
    </div>
  );
}

/* ---------------- Edit Profile ---------------- */
function EditProfile({ p, onSaved }) {
  const [form, setForm] = useState(() => pick(p));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = buildPayload(form);
      await api.patch(`/players/${p.id}`, payload);
      toast.success("Profile updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="edit-profile-panel">
      <h2 className="font-display text-2xl uppercase tracking-tight">My Profile</h2>
      <div className="mt-4 max-w-lg">
        <ImageUploadField label="Profile Photo" testid="upload-photo" value={form.profile_photo_url} onChange={(url) => set("profile_photo_url", url)} uploadUrl={`/players/${p.id}/media/upload-file`} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
        <Field label="First Name"><input data-testid="edit-first_name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inp} /></Field>
        <Field label="Last Name"><input data-testid="edit-last_name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inp} /></Field>
        <Field label="Graduation Year"><input data-testid="edit-graduation_year" type="number" value={form.graduation_year} onChange={(e) => set("graduation_year", e.target.value)} className={inp} /></Field>
        <Field label="School"><input data-testid="edit-school" value={form.school} onChange={(e) => set("school", e.target.value)} className={inp} /></Field>
        <Field label="Province"><Select testid="edit-province" value={form.province} onChange={(v) => set("province", v)} options={PROVINCES} /></Field>
        <Field label="City"><input data-testid="edit-city" value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} /></Field>
        <Field label="Height (cm)"><input data-testid="edit-height_cm" type="number" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} className={inp} /></Field>
        <Field label="Weight (kg)"><input data-testid="edit-weight_kg" type="number" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className={inp} /></Field>
        <Field label="GPA"><input data-testid="edit-gpa" type="number" step="0.1" value={form.gpa} onChange={(e) => set("gpa", e.target.value)} className={inp} /></Field>
        <Field label="Bats"><Select testid="edit-bats" value={form.bats} onChange={(v) => set("bats", v)} options={["L", "R", "S"]} /></Field>
        <Field label="Throws"><Select testid="edit-throws" value={form.throws} onChange={(v) => set("throws", v)} options={["L", "R"]} /></Field>
        <Field label="Primary Position"><Select testid="edit-primary_position" value={form.primary_position} onChange={(v) => set("primary_position", v)} options={POSITIONS} /></Field>
        <Field label="Secondary Position"><Select testid="edit-secondary_position" value={form.secondary_position} onChange={(v) => set("secondary_position", v)} options={POSITIONS} /></Field>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4 max-w-2xl">
        <div>
          <div className="label-overline">Date of Birth <span className="normal-case text-slate-400">(private)</span></div>
          <input data-testid="edit-date_of_birth" type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} className={`${inp} mt-1`} />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 sm:pt-6">
          <input data-testid="edit-show_age_publicly" type="checkbox" checked={!!form.show_age_publicly} onChange={(e) => set("show_age_publicly", e.target.checked)} />
          Show age (not birthdate) on my public profile
        </label>
        <div className="sm:col-span-2">
          <div className="label-overline">Current Team</div>
          <div className="mt-1"><TeamSelector value={form.current_team_id} initialTeam={p.current_team_info} onChange={(t) => set("current_team_id", t?.id || "")} testidPrefix="edit-team" /></div>
        </div>
      </div>

      <SaveBar busy={busy} onSave={save} />
    </div>
  );
}

/* ---------------- Recruiting & Social ---------------- */
function RecruitingLinks({ p, onSaved }) {
  const [form, setForm] = useState(() => pick(p));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/players/${p.id}`, buildPayload(form)); toast.success("Recruiting links updated"); onSaved(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const LINKS = [
    ["instagram_handle", "Instagram (@handle)"], ["x_handle", "X / Twitter (@handle)"], ["tiktok_handle", "TikTok (@handle)"], ["youtube_handle", "YouTube (handle/URL)"],
    ["fieldlevel_url", "FieldLevel"], ["ncsa_url", "NCSA"], ["img_url", "IMG Academy"], ["perfect_game_url", "Perfect Game"],
    ["prep_baseball_url", "Prep Baseball"], ["personal_website_url", "Personal Website"], ["hudl_url", "Hudl"], ["highlight_video_url", "Highlight Video"],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="recruiting-panel">
      <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2"><LinkIcon className="w-5 h-5 text-[#E63946]" /> Recruiting & Social</h2>
      <p className="text-xs text-slate-500 mt-1">Add your recruiting service and social links so coaches can find everything in one place.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        {LINKS.map(([k, label]) => (
          <Field key={k} label={label}><input data-testid={`edit-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className={inp} placeholder="—" /></Field>
        ))}
      </div>
      <SaveBar busy={busy} onSave={save} />
    </div>
  );
}

/* ---------------- Privacy ---------------- */
function Privacy({ p, onSaved }) {
  const [form, setForm] = useState(() => pick(p));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/players/${p.id}`, buildPayload(form)); toast.success("Privacy settings updated"); onSaved(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const TOGGLES = [
    ["public_visible", "Public Profile Visibility", "Show my profile in public prospect listings and search."],
    ["media_release_consent", "Media Release Consent", "Allow CBR to use my photos/videos in media and social posts."],
    ["social_tagging_enabled", "Social Tagging", "Allow CBR to tag me in social media posts."],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 max-w-2xl" data-testid="privacy-panel">
      <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#E63946]" /> Privacy</h2>
      <div className="mt-5 space-y-3">
        {TOGGLES.map(([k, label, desc]) => (
          <label key={k} className="flex items-start gap-3 border border-slate-200 rounded-sm p-4 cursor-pointer">
            <input data-testid={`toggle-${k}`} type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1 w-4 h-4" />
            <div><div className="text-sm font-semibold">{label}</div><div className="text-xs text-slate-500 mt-0.5">{desc}</div></div>
          </label>
        ))}
      </div>
      <SaveBar busy={busy} onSave={save} />
    </div>
  );
}

/* ---------------- helpers & primitives ---------------- */
const inp = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm";

function pick(p) {
  const o = {};
  EDITABLE.forEach((k) => { o[k] = p[k] ?? (typeof p[k] === "boolean" ? p[k] : ""); });
  o.public_visible = p.public_visible !== false;
  o.media_release_consent = !!p.media_release_consent;
  o.social_tagging_enabled = p.social_tagging_enabled !== false;
  return o;
}

function buildPayload(form) {
  const out = {};
  Object.entries(form).forEach(([k, v]) => {
    if (["public_visible", "media_release_consent", "social_tagging_enabled", "show_age_publicly"].includes(k)) { out[k] = !!v; return; }
    if (v === "" || v === null || v === undefined) return;
    if (["graduation_year"].includes(k)) out[k] = Number(v);
    else if (["height_cm", "weight_kg", "gpa"].includes(k)) out[k] = Number(v);
    else out[k] = v;
  });
  return out;
}

function Field({ label, children }) {
  return <div><label className="label-overline text-[10px]">{label}</label><div className="mt-1">{children}</div></div>;
}
function Select({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inp} bg-white`}>
      <option value="">—</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function SaveBar({ busy, onSave }) {
  return (
    <div className="mt-6 flex justify-end">
      <button data-testid="save-profile-btn" onClick={onSave} disabled={busy} className="bg-[#E63946] text-white px-6 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">{busy ? "Saving…" : "Save Changes"}</button>
    </div>
  );
}
function Card({ title, icon: Icon, note, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="label-overline flex items-center gap-2"><Icon className="w-4 h-4 text-[#E63946]" /> {title}</div>
        {note && <span className="text-[9px] uppercase tracking-wider text-slate-400">{note}</span>}
      </div>
      {children}
    </div>
  );
}
function Metric({ label, data }) {
  return (
    <div className="border border-slate-100 rounded-sm p-3 text-center">
      <div className="label-overline text-[9px]">{label}</div>
      <div className="font-data text-2xl mt-1 text-[#0A192F]">{data?.value ?? "—"}</div>
      {data?.units && <div className="text-[10px] text-slate-400">{data.units}</div>}
    </div>
  );
}
function Section({ label, children }) {
  return <div className="mb-4 last:mb-0"><div className="label-overline text-[10px] mb-2">{label}</div><div className="space-y-2">{children}</div></div>;
}
function Row({ title, sub, tag, to }) {
  const inner = (
    <div className="flex items-center justify-between gap-2 border border-slate-100 rounded-sm p-2.5">
      <div className="min-w-0"><div className="text-sm font-semibold truncate">{title}</div>{sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}</div>
      {tag && <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-sm shrink-0">{tag}</span>}
    </div>
  );
  return to ? <Link to={to} className="block hover:opacity-80">{inner}</Link> : inner;
}
function Empty({ text }) {
  return <p className="text-xs text-slate-400 py-2">{text}</p>;
}
